import { useState, useRef, useEffect } from 'react';

const PROVIDERS = [
  { value: 'claude', label: 'Claude (Anthropic)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'deepseek', label: 'DeepSeek' },
  { value: 'mistral', label: 'Mistral' },
  { value: 'grok', label: 'Grok (xAI)' },
  { value: 'custom', label: 'Custom (OpenAI-compatible)' },
];

const QUICK_PROMPTS = [
  { label: 'Analyse du setup', text: 'Analyze the current chart setup. Identify the trend, key support/resistance levels, and notable patterns.' },
  { label: 'Entree / Sortie', text: 'Based on the current chart context, what would be a good entry point? Where should I place my stop loss and take profit?' },
  { label: 'Risque', text: 'Assess the risk of my current open positions. Is the risk/reward ratio favorable? Any concerns?' },
  { label: 'Indicateurs', text: 'Analyze the indicator values. Are there any divergences, overbought/oversold conditions, or signal confirmations?' },
];

function SettingsForm({ provider, model, apiKey, baseUrl, onProviderChange, onModelChange, onApiKeyChange, onBaseUrlChange, onSave, DEFAULT_MODELS, PROVIDER_MODELS }) {
  const models = PROVIDER_MODELS[provider] || [];
  return (
    <div className="ai-settings-form">
      <div className="ai-field">
        <label>Provider</label>
        <select value={provider} onChange={e => { onProviderChange(e.target.value); }}>
          {PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
      <div className="ai-field">
        <label>Model</label>
        {models.length > 0 ? (
          <select value={model} onChange={e => onModelChange(e.target.value)}>
            {models.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        ) : (
          <input
            type="text"
            value={model}
            onChange={e => onModelChange(e.target.value)}
            placeholder={DEFAULT_MODELS[provider] || 'model-name'}
          />
        )}
      </div>
      <div className="ai-field">
        <label>API Key</label>
        <input
          type="password"
          value={apiKey}
          onChange={e => onApiKeyChange(e.target.value)}
          placeholder="sk-..."
        />
      </div>
      {provider === 'custom' && (
        <div className="ai-field">
          <label>Base URL</label>
          <input
            type="text"
            value={baseUrl}
            onChange={e => onBaseUrlChange(e.target.value)}
            placeholder="https://api.example.com/v1"
          />
        </div>
      )}
      <button className="btn btn-sm btn-primary" onClick={onSave} style={{ marginTop: 6 }}>
        Save Settings
      </button>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <div className={`ai-message ${isUser ? 'ai-message-user' : 'ai-message-assistant'}`}>
      <div className="ai-message-label">{isUser ? 'You' : 'AI'}</div>
      <div className="ai-message-content">
        {message.content || <span className="ai-typing">...</span>}
      </div>
    </div>
  );
}

export default function AIAnalysisPanel({
  provider, model, apiKey, baseUrl,
  setProvider, setModel, setApiKey, setBaseUrl, saveSettings,
  messages, streaming, error,
  sendMessage, clearChat, stopStreaming,
  isConfigured, DEFAULT_MODELS, PROVIDER_MODELS,
}) {
  const [input, setInput] = useState('');
  const [showSettings, setShowSettings] = useState(!isConfigured);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const handleSend = () => {
    if (!input.trim() || streaming) return;
    sendMessage(input);
    setInput('');
  };

  const handleQuickPrompt = (text) => {
    if (streaming) return;
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="ai-panel">
      <div className="ai-panel-header">
        <span className="ai-panel-title">AI Analysis</span>
        <div className="ai-panel-actions">
          {messages.length > 0 && (
            <button className="btn-icon" onClick={clearChat} title="Clear chat">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
            </button>
          )}
          <button
            className={`btn-icon ${showSettings ? 'active' : ''}`}
            onClick={() => setShowSettings(v => !v)}
            title="Settings"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.32 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
          </button>
        </div>
      </div>

      {showSettings && (
        <SettingsForm
          provider={provider} model={model} apiKey={apiKey} baseUrl={baseUrl}
          onProviderChange={setProvider} onModelChange={setModel}
          onApiKeyChange={setApiKey} onBaseUrlChange={setBaseUrl}
          onSave={() => { saveSettings(); if (isConfigured) setShowSettings(false); }}
          DEFAULT_MODELS={DEFAULT_MODELS}
          PROVIDER_MODELS={PROVIDER_MODELS}
        />
      )}

      <div className="ai-chat-area">
        {messages.length === 0 && !showSettings && (
          <div className="ai-empty">
            <div className="ai-empty-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4"><path d="M12 2a10 10 0 0110 10 10 10 0 01-10 10A10 10 0 012 12 10 10 0 0112 2z"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
            </div>
            <p>Ask the AI to analyze your chart, positions, or indicators.</p>
            <div className="ai-quick-prompts">
              {QUICK_PROMPTS.map((qp, i) => (
                <button
                  key={i}
                  className="ai-quick-btn"
                  onClick={() => handleQuickPrompt(qp.text)}
                  disabled={!isConfigured || streaming}
                >
                  {qp.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {error && <div className="ai-error">{error}</div>}
        <div ref={chatEndRef} />
      </div>

      <div className="ai-input-area">
        {streaming ? (
          <button className="btn btn-sm btn-danger ai-stop-btn" onClick={stopStreaming}>
            Stop
          </button>
        ) : (
          <>
            <textarea
              className="ai-input"
              rows={2}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isConfigured ? 'Ask something...' : 'Configure your API key in settings first'}
              disabled={!isConfigured}
            />
            <button
              className="ai-send-btn"
              onClick={handleSend}
              disabled={!input.trim() || !isConfigured}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
